import React, { FunctionComponent, useState } from 'react';
import { PopUpHandler } from '../../../shared/PopupHandler';
import { useTranslations } from '../../../../hooks/useTranslationsHelper';
import { MediatorType } from '../../types';
import './LimitsHistory.scss';
import { useAuth } from 'react-oidc-context';
import { useUserBudgetLimitsQuery } from '../../../../libs/graphql/baseAppAPI/queries/__generated__/obtaining-limits.query.generated';
import { xTenantId } from '../../../../consts';
import { formatCurrency } from '../../../../utils/wallets';
import { LogoSpinner } from '../../../shared/LogoSpinner/LogoSpinner';

type LimitsHistoryProps = {
  show: boolean;
  onClose(): void;
  mediatorType?: MediatorType;
};

type Period = 'Daily' | 'Weekly' | 'Monthly';

type Row = {
  amount: number | null;
  dateStr: string | null;
  ts: number;
};

type Sections = Record<Period, Row[]>;

const initialSections: Sections = { Daily: [], Weekly: [], Monthly: [] };

export const LimitsHistory: FunctionComponent<LimitsHistoryProps> = ({ show, onClose, mediatorType }) => {
  const { t } = useTranslations();
  const auth = useAuth();
  const userProfileId = auth.user?.profile?.user_profile_id as string;

  const [sections, setSections] = useState<Sections>(initialSections);

  const headingMap: Record<string, string> = {
    sports: 'Sports Loss Limits',
    casino: 'Casino Loss Limits',
    monetary: 'Deposit Limits',
  };

  const { loading } = useUserBudgetLimitsQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: { xTenantId, userProfileId },
    onCompleted: (res) => {
      const periodMap: Record<'P1D' | 'P7D' | 'P30D', Period> = {
        P1D: 'Daily',
        P7D: 'Weekly',
        P30D: 'Monthly',
      };

      const buckets: Sections = { Daily: [], Weekly: [], Monthly: [] };

      res?.userBudgetLimits?.forEach((item) => {
        if (item && item.mediatorType === mediatorType && item.activationStatus === 'CANCELED') {
          const key = item.period as keyof typeof periodMap | undefined;
          const group = key ? periodMap[key] : undefined;

          if (!group) return;

          let dateStr: string | null = null;
          let ts = 0;

          if (item.lastUpdatedOn) {
            const d = new Date(item.lastUpdatedOn);
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();

            dateStr = `${day}/${month}/${year}`;
            ts = d.getTime();
          }

          buckets[group].push({ amount: item.amount ?? null, dateStr, ts });
        }
      });

      (['Daily', 'Weekly', 'Monthly'] as Period[]).forEach((p) => {
        buckets[p].sort((a, b) => b.ts - a.ts);
      });

      setSections(buckets);
    },
  });

  const hasAny = sections.Daily.length > 0 || sections.Weekly.length > 0 || sections.Monthly.length > 0;

  return (
    <PopUpHandler
      header={`${headingMap[String(mediatorType)] ?? t('limits.history')} History`}
      drawerProps={{ backdropCanClose: true, className: 'limits-history' }}
      headingProps={{ headingLevel: 5 }}
      isOpen={show}
      onClose={onClose}
    >
      <div className="limits-history__body" style={{ background: 'transparent' }}>
        {loading && (
          <div className="limits-history__no-results">
            <LogoSpinner />
          </div>
        )}
        <div className={`limits-history__content${!loading && hasAny ? ' limits-history__content--loaded' : ''}`}>
          {!loading && hasAny && (
            <>
              {sections.Daily.length > 0 && (
                <section className="limits-history__section">
                  <h6 className="limits-history__subheading">Daily</h6>
                  <ul className="limits-history__list">
                    {sections.Daily.map((row, i) => (
                      <li key={`d-${i}`} className="limits-history__item">
                        <span className="limits-history__amount">
                          {row.amount !== null ? formatCurrency(Number(row.amount)) : '—'}
                        </span>
                        <span className="limits-history__date">{row.dateStr ?? '—'}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {sections.Weekly.length > 0 && (
                <section className="limits-history__section">
                  <h6 className="limits-history__subheading">Weekly</h6>
                  <ul className="limits-history__list">
                    {sections.Weekly.map((row, i) => (
                      <li key={`w-${i}`} className="limits-history__item">
                        <span className="limits-history__amount">
                          {row.amount !== null ? formatCurrency(Number(row.amount)) : '—'}
                        </span>
                        <span className="limits-history__date">{row.dateStr ?? '—'}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {sections.Monthly.length > 0 && (
                <section className="limits-history__section">
                  <h6 className="limits-history__subheading">Monthly</h6>
                  <ul className="limits-history__list">
                    {sections.Monthly.map((row, i) => (
                      <li key={`m-${i}`} className="limits-history__item">
                        <span className="limits-history__amount">
                          {row.amount !== null ? formatCurrency(Number(row.amount)) : '—'}
                        </span>
                        <span className="limits-history__date">{row.dateStr ?? '—'}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}{' '}
        </div>
        {!hasAny && !loading && (
          <div className="limits-history__no-results">
            <p className="limits-history__no-results-text">No results</p>
          </div>
        )}
      </div>
    </PopUpHandler>
  );
};
